import React from 'react';
import styled from 'styled-components';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import { ChatMessages } from '@/features/ai-agents/chat/chat-messages';
import {
  ChatInput,
  ChatInputHandle,
} from '@/features/ai-agents/components/chat-input';
import { useFileDropDetector } from '@/features/ai-agents/shared/use-file-drop-detector';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';

export interface ChatAreaProps {
  enableFilesUpload?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;
  showReasoning?: boolean;
  showTools?: boolean;

  inputBottom?: string;
  inputLeft?: string;
  inputRight?: string;

  starters?: AiConversationStarter;
}

export function ChatArea({
  enableFilesUpload,
  enableExamples,
  enableVoiceInput,
  showReasoning,
  showTools,
  inputBottom,
  inputLeft,
  inputRight,
  starters,
}: ChatAreaProps) {
  const dropzoneVisible = useFileDropDetector();
  const {
    agent: selectedAgent,
    isLoadingSession,
    sendMessage: sendUserMessage,
    isProcessing,
    abortMessage,
    loadSession,
    currentSessionId,
    previousSessionId,
    messages,
  } = useChatSession();

  // const [showScrollButton, setShowScrollButton] = useState(false);

  useSignalEffect(() => {
    if (
      currentSessionId.value !== previousSessionId.value &&
      !isLoadingSession.peek()
    ) {
      loadSession().then();
    }
  });

  const showStarters = useComputed(
    () => messages?.value?.length <= 0 && !!starters,
  );

  const chatRef = React.useRef<ChatInputHandle>(null);

  const starterSendMessage = (prompt: string, sendPrompt = true) => {
    chatRef.current?.setPrompt(prompt);
    if (sendPrompt) {
      setTimeout(() => {
        chatRef.current?.send();
      }, 100);
    }
  };

  const starterAttachFile = () => {
    chatRef.current?.openDropzone();
  };

  const starterAttachSketch = () => {
    chatRef.current?.openWhiteboard();
  };

  return (
    <Wrapper>
      {isLoadingSession.value && (
        <BackdropSpinner
          loading
          blurAmount={0}
          spinnerSize={40}
          backgroundColor="rgba(0,0,0,0)"
        />
      )}
      {showStarters.value &&
        starters(starterSendMessage, starterAttachFile, starterAttachSketch)}
      {!showStarters.value && (
        <ChatMessages showTools={showTools} showReasoning={showReasoning} />
      )}
      <InputWrapper
        inputBottom={inputBottom}
        inputLeft={inputLeft}
        inputRight={inputRight}
      >
        <ChatInput
          ref={chatRef}
          disabled={!(selectedAgent || isLoadingSession.value)}
          showDropZone={dropzoneVisible}
          onSubmit={sendUserMessage}
          onAbort={abortMessage}
          loading={isProcessing.value}
          enableFilesUpload={enableFilesUpload}
          enableExamples={enableExamples}
          enableVoiceInput={enableVoiceInput}
        />
      </InputWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: grid;
`;

const InputWrapper = styled.div`
  position: absolute;
  bottom: ${(p) => p.inputBottom || `3rem`};
  left: ${(p) => p.inputLeft || `0`};
  right: ${(p) => p.inputRight || `0`};
`;
